import React, { FC, useState, useEffect, useRef } from 'react'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { GeoSearch } from 'features/shared/GeoSearch'
import { Icon } from 'components/Icon'
import { useOnClickOutside } from 'hooks/useClickOutside'
import { Flex } from 'components/Flex'
import styles from './SearchTool.module.scss'
import { useProjectContext } from 'context/project-context'
import { LocationSearch } from 'models/geosearch'
import { fire } from 'utils/customEvent'
import { FlyToBounds, Events } from 'constants/events'

export const SearchTool: FC = () => {
  const { t } = useTranslation()
  const [{ search }, projectDispatch] = useProjectContext()
  const [isOpen, setOpen] = useState(false)
  const searchToolRef = useRef<HTMLDivElement>({} as HTMLDivElement)

  const close = () => {
    projectDispatch.setSearch(null)
    setOpen(false)
  }

  const onClickOutside = () => {
    if (search) return
    close()
  }

  useOnClickOutside(searchToolRef, onClickOutside)

  useEffect(() => {
    const onKeyDownTest = (event: any) => {
      if (event.code === 'Escape') {
        close()
      }
    }

    document.addEventListener('keydown', onKeyDownTest)
    return () => document.removeEventListener('keydown', onKeyDownTest)
  })

  const onSelect = (location: LocationSearch | null) => {
    projectDispatch.setSearch(location)

    if (location) {
      fire<FlyToBounds>(Events.FlyToBounds, { bounds: location.bounds })
    }
  }

  return (
    <div className={cx(styles.searchBarTool, isOpen ? styles.open : '')} ref={searchToolRef}>
      {!isOpen && !search && (
        <button
          className={styles.searchToggleButton}
          aria-label={t('map.search.open')}
          onClick={() => setOpen(true)}
        >
          <Icon name="search-line" size="xl" className={styles.icon} />
        </button>
      )}

      {(isOpen || search) && (
        <Flex align="center">
          <Icon name="search-line" size="xl" className={styles.icon} />

          <GeoSearch
            onSelect={onSelect}
            name="geosearch"
            placeholder={t('map.search.label')}
            className={styles.input}
            initialValue={search?.name}
          />
          {search && (
            <button onClick={close} className={styles.closeButton}>
              <Flex align="center">
                <Icon name="close-line" />
              </Flex>
            </button>
          )}
        </Flex>
      )}
    </div>
  )
}

SearchTool.displayName = 'SearchTool'
